import { useMemo, useState } from 'react';
import { useRouter } from 'next/router';

import {
  BannerGroup,
  FilterParam,
  ShopInfo,
  TextCategoryList,
  useComponentList,
  useStoreHomeUBL,
} from '@domains/storehome-common';

import { useBannerGroupPreview } from '../hooks/useBannerGroupPreview';

interface UseMainServiceParams {
  shop_info: ShopInfo;
  banner_group: BannerGroup | null;
  is_preview?: boolean;
  category_list?: TextCategoryList[];
}

const BEST_CATEGORY_ID = '-1';

export const useMainService = ({ shop_info, banner_group, is_preview, category_list }: UseMainServiceParams) => {
  const router = useRouter();
  const { query } = router;

  const { id, name } = shop_info;
  const { categoryClickUBL, categoryImpressionUBL, zigzinFilterClickUBL, searchClickUBL } = useStoreHomeUBL(id);
  const hrefCallback = (category_id: string) => {
    if (category_id === BEST_CATEGORY_ID) {
      return `/${query.main_domain}/best`;
    }
    return `/${query.main_domain}/category/${category_id}`;
  };
  const categories = useMemo(() => {
    return category_list?.map((category) => ({ label: category.name, value: category.id })) ?? [];
  }, [category_list]);

  const [filter_params, setFilterParams] = useState({
    check_button_item_ids: [] as string[],
    sub_filter_id_list: [] as string[],
    sorting_item_id: null as string | null,
  });

  const params = {
    shop_id: id,
    ...filter_params,
    ...(validateProductId(query.pdp_product_id) && { pdp_product_id: query.pdp_product_id as string }),
  };

  const { fetchNextPage, ...component_list_props } = useComponentList(params);
  const applied_banner_group = useBannerGroupPreview(is_preview ?? false, banner_group);

  const exists_banner = !!applied_banner_group;

  const onParamsChange = (change_param: FilterParam) => {
    setFilterParams(change_param);

    if (filter_params.sub_filter_id_list.length !== change_param.sub_filter_id_list.length) {
      zigzinFilterClickUBL(Boolean(change_param.sub_filter_id_list.length));
    }
  };

  const handleCategoryClick = (id: string, name: string, index: number) => {
    categoryClickUBL({
      id,
      name,
      index,
    });
    if (id === BEST_CATEGORY_ID) {
      router.push(`/${query.main_domain}/best`);
      return;
    }
    router.push(`/${query.main_domain}/category/${id}`);
  };

  const handleImpressionCategory = (id: string, name: string, index: number) => {
    categoryImpressionUBL({ id, name, index });
  };

  const handleSearchClick = () => {
    searchClickUBL();
    router.push(`/${query.main_domain}/search`);
  };

  return {
    query,
    params,
    shop_id: id,
    shop_name: name,
    categories,
    applied_banner_group,
    component_list_props,
    exists_banner,
    onParamsChange,
    hrefCallback,
    categoryClickUBL,
    categoryImpressionUBL,
    fetchNextPage,
    handleCategoryClick,
    handleImpressionCategory,
    handleSearchClick,
    searchClickUBL,
  };
};

const validateProductId = (productId: undefined | null | string | string[]): boolean => {
  if (!productId) {
    return false;
  }

  const stringValue = Array.isArray(productId) ? productId[0] : productId;

  if (!stringValue || typeof stringValue !== 'string' || stringValue.trim() === '') {
    return false;
  }

  const trimmed = stringValue.trim();
  if (!/^[0-9]+$/.test(trimmed)) {
    return false;
  }

  return true;
};
